import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Paper,
  Divider,
  Link,
  TextField,
  Chip,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Collapse,
  Grid,
  IconButton,
  Button,
  Fade,
  Grow,
  Slide
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Download,
  Launch,
  Code,
  Api,
  ExpandMore,
  ExpandLess,
  PlayArrow,
  Send,
  Folder,
  FolderOpen,
  Http,
  Security,
  Speed,
  IntegrationInstructions,
  MenuBook,
  CheckCircle,
  RadioButtonUnchecked,
  GetApp,
  Help,
  Visibility,
  VisibilityOff,
  Info,
  ContentCopy,
  Refresh,
  FilterList,
  Settings,
  ChevronRight
} from '@mui/icons-material';
import { API_BASE_URL } from '../config/api';
import axios from 'axios';

// Waltero Design System Components
import {
  WalteroCard,
  WalteroButton,
  WalteroTypography,
  WalteroStatusChip,
  WalteroSkeleton,
  useResponsive
} from './waltero';

// Accessibility utilities
import {
  createAccessibleLabel,
  createFocusTrap,
  announceToScreenReader,
  generateAccessibleId
} from '../utils/accessibility';

const ApiDocumentation = ({ user, token, organizations }) => {
  const theme = useTheme();
  const responsive = useResponsive();
  
  // Component state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedEndpoint, setSelectedEndpoint] = useState(null);
  const [requestBody, setRequestBody] = useState('');
  const [requestParams, setRequestParams] = useState({});
  const [response, setResponse] = useState(null);
  const [requestLoading, setRequestLoading] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState(['Authentication']);
  const [selectedTreeNodes, setSelectedTreeNodes] = useState([]);
  const [apiEndpoints, setApiEndpoints] = useState({});
  const [collectionLoading, setCollectionLoading] = useState(true);
  
  // Enhanced documentation state
  const [showRequestDetails, setShowRequestDetails] = useState(false);
  const [showResponseDetails, setShowResponseDetails] = useState(false);
  const [helpTooltips, setHelpTooltips] = useState({});
  const [requestHeaders, setRequestHeaders] = useState({
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token || 'your-jwt-token'}`
  });
  const [showAdminEndpoints, setShowAdminEndpoints] = useState(true); // Default to showing all endpoints
  
  // UI state
  const [showMobileNav, setShowMobileNav] = useState(false);
  const [animationKey, setAnimationKey] = useState(0);
  
  // Accessibility IDs
  const componentId = generateAccessibleId('api-docs');
  const treeId = generateAccessibleId('endpoint-tree');
  const contentId = generateAccessibleId('endpoint-content');

  // Parse parameters from documentation tables
  const parseDocumentationParameters = (description) => {
    if (!description) return {};
    
    const params = {};
    
    // Look for parameter tables in the description
    const parameterSections = [
      'Query Parameters',
      'Path Parameters', 
      'Parameters',
      'Request Body Fields'
    ];
    
    parameterSections.forEach(sectionName => {
      const sectionRegex = new RegExp(`##\\s*[🔍📋]*\\s*${sectionName}[\\s\\S]*?(?=##|$)`, 'i');
      const sectionMatch = description.match(sectionRegex);
      
      if (sectionMatch) {
        const sectionContent = sectionMatch[0];
        
        // Extract table rows
        const tableRows = sectionContent.split('\n').filter(line => 
          line.includes('|') && 
          !line.includes('---') && 
          line.split('|').length >= 4
        );
        
        tableRows.forEach(row => {
          const cells = row.split('|').map(cell => cell.trim()).filter(cell => cell);
          
          if (cells.length >= 4) {
            const [paramName, type, required, defaultValue, description, example] = cells;
            
            // Clean parameter name
            const cleanParamName = paramName.replace(/`/g, '').trim();
            
            if (cleanParamName && !cleanParamName.toLowerCase().includes('parameter')) {
              // Use example value if available, otherwise use default, otherwise generate based on type
              let defaultVal = '';
              
              if (example && example !== '-') {
                defaultVal = example.replace(/`/g, '').trim();
              } else if (defaultValue && defaultValue !== '-') {
                defaultVal = defaultValue.replace(/`/g, '').trim();
              } else {
                // Generate default based on type
                const typeStr = type.toLowerCase();
                if (typeStr.includes('integer')) {
                  defaultVal = '1';
                } else if (typeStr.includes('uuid')) {
                  defaultVal = '62dd1242-b0ba-4412-aea0-35479b4c5117';
                } else if (typeStr.includes('string')) {
                  defaultVal = '';
                } else if (typeStr.includes('boolean')) {
                  defaultVal = 'true';
                }
              }
              
              params[cleanParamName] = defaultVal;
            }
          }
        });
      }
    });
    
    return params;
  };

  // Parse parameter metadata for enhanced UI
  const parseParameterMetadata = (description) => {
    if (!description) return {};
    
    const metadata = {};
    
    // Look for parameter tables in the description
    const parameterSections = [
      { name: 'Query Parameters', type: 'query' },
      { name: 'Path Parameters', type: 'path' }, 
      { name: 'Parameters', type: 'mixed' },
      { name: 'Request Body Fields', type: 'body' }
    ];
    
    parameterSections.forEach(section => {
      const sectionRegex = new RegExp(`##\\s*[🔍📋]*\\s*${section.name}[\\s\\S]*?(?=##|$)`, 'i');
      const sectionMatch = description.match(sectionRegex);
      
      if (sectionMatch) {
        const sectionContent = sectionMatch[0];
        
        // Extract table rows
        const tableRows = sectionContent.split('\n').filter(line => 
          line.includes('|') && 
          !line.includes('---') && 
          line.split('|').length >= 4
        );
        
        tableRows.forEach(row => {
          const cells = row.split('|').map(cell => cell.trim()).filter(cell => cell);
          
          if (cells.length >= 4) {
            const [paramName, type, required, defaultValue, description, example] = cells;
            
            // Clean parameter name
            const cleanParamName = paramName.replace(/`/g, '').trim();
            
            if (cleanParamName && !cleanParamName.toLowerCase().includes('parameter')) {
              metadata[cleanParamName] = {
                type: type.replace(/`/g, '').trim(),
                required: required.includes('Yes') || required.includes('✅'),
                description: description ? description.replace(/`/g, '').trim() : '',
                example: example ? example.replace(/`/g, '').trim() : '',
                parameterType: section.type,
                defaultValue: defaultValue && defaultValue !== '-' ? defaultValue.replace(/`/g, '').trim() : ''
              };
            }
          }
        });
      }
    });
    
    return metadata;
  };

  // Parse Postman collection to extract endpoints
  const parsePostmanCollection = (collection) => {
    const endpoints = {};
    
    const parseItems = (items, categoryPrefix = '') => {
      items.forEach(item => {
        if (item.item) {
          // This is a folder/category
          // Clean up category name and improve grouping
          let cleanName = item.name.replace(/^[🔐👤🏢📱📡📶🤖📊📈📋]\s*/, '').trim();
          
          // Determine the main category
          let mainCategory = categoryPrefix || cleanName;
          
          // Group organization endpoints under a single "Organizations" category
          if (cleanName.toLowerCase().includes('organization') || categoryPrefix?.toLowerCase().includes('organization')) {
            mainCategory = 'Organizations';
          }
          
          // Initialize category if it doesn't exist
          if (!endpoints[mainCategory]) {
            endpoints[mainCategory] = [];
          }
          
          // Recursively parse sub-items with the main category
          parseItems(item.item, mainCategory);
        } else if (item.request) {
          // This is an actual request
          let parentCategory = categoryPrefix || 'Other';
          
          // Merge similar organization categories
          if (parentCategory.toLowerCase().includes('organization')) {
            parentCategory = 'Organizations';
          }
          
          if (!endpoints[parentCategory]) {
            endpoints[parentCategory] = [];
          }
          
          // Extract URL path from Postman format (excluding query parameters)
          let path = '';
          if (item.request.url) {
            if (typeof item.request.url === 'string') {
              const urlStr = item.request.url.replace('{{base_url}}/api', '').replace('{{base_url}}', '');
              path = urlStr.split('?')[0]; // Remove query params from path
            } else if (item.request.url.raw) {
              const urlStr = item.request.url.raw.replace('{{base_url}}/api', '').replace('{{base_url}}', '');
              path = urlStr.split('?')[0]; // Remove query params from path
            } else if (item.request.url.path) {
              path = '/' + item.request.url.path.filter(p => p !== 'api').join('/');
            }
          }
          
          // Extract parameters from URL and query
          const params = {};
          const variables = collection.variable || [];
          
          // Extract path parameters
          const pathParams = path.match(/\{\{(\w+)\}\}/g);
          if (pathParams) {
            pathParams.forEach(param => {
              const paramName = param.replace(/[{}]/g, '');
              const variable = variables.find(v => v.key === paramName);
              if (variable) {
                params[paramName] = variable.value;
                path = path.replace(param, `{${paramName}}`);
              }
            });
          }
          
          // Extract query parameters from URL object
          if (item.request.url && typeof item.request.url === 'object' && item.request.url.query) {
            item.request.url.query.forEach(q => {
              if (q.key) {
                const value = q.value || '';
                params[q.key] = value.replace(/\{\{(\w+)\}\}/g, (match, varName) => {
                  const variable = variables.find(v => v.key === varName);
                  return variable ? variable.value : match;
                });
                
                // Debug logging for date parameters
                if (q.key.toLowerCase().includes('date')) {
                  console.log('📅 Found date parameter:', q.key, '=', params[q.key], 'for endpoint:', item.name);
                }
              }
            });
          }

          // Parse parameters from documentation tables
          const documentationParams = parseDocumentationParameters(item.request.description);
          Object.assign(params, documentationParams);
          
          // Extract sample body
          let sampleBody = '';
          if (item.request.body && item.request.body.raw) {
            sampleBody = item.request.body.raw;
            // Replace variables in body
            variables.forEach(variable => {
              sampleBody = sampleBody.replace(new RegExp(`{{${variable.key}}}`, 'g'), variable.value);
            });
          }
          
          // Check if this is an admin-only endpoint
          const isAdminOnly = item.request.description?.includes('**Requires admin organization access.**') ||
                            item.request.description?.includes('Admin)') ||
                            item.name.includes('(Admin)') ||
                            path.includes('/debug-') ||
                            path.includes('/success-rate') ||
                            path.includes('/recommendations') ||
                            path.includes('/visual-review') ||
                            path.includes('/sensor-graphs') ||
                            path.includes('/update-measures');

          // Parse parameter metadata from documentation
          const parameterMetadata = parseParameterMetadata(item.request.description);

          // Debug logging for endpoints with missing date params
          if (item.name.toLowerCase().includes('consumption') || item.name.toLowerCase().includes('usage')) {
            console.log('🔍 Debug endpoint:', item.name);
            console.log('  - Extracted params:', Object.keys(params));
            console.log('  - Has query array:', !!item.request.url?.query);
            console.log('  - Query array length:', item.request.url?.query?.length || 0);
          }

          const endpoint = {
            name: item.name,
            method: item.request.method,
            path: path,
            description: item.request.description || `${item.request.method} ${path}`,
            params: params,
            parameterMetadata: parameterMetadata,
            sampleBody: sampleBody,
            requiresAuth: path !== '/health',
            isAdminOnly: isAdminOnly,
            headers: item.request.header || []
          };
          
          endpoints[parentCategory].push(endpoint);
          
          // Debug logging for Organizations
          if (parentCategory === 'Organizations') {
            console.log('🏢 Added to Organizations:', endpoint.name, endpoint.path, endpoint.method);
          }
        }
      });
    };
    
    if (collection.item) {
      parseItems(collection.item);
    }
    
    // Remove empty categories and return cleaned endpoints
    const cleanedEndpoints = {};
    Object.entries(endpoints).forEach(([category, endpointList]) => {
      if (endpointList && endpointList.length > 0) {
        cleanedEndpoints[category] = endpointList;
      }
    });
    
    return cleanedEndpoints;
  };

  // Load Postman collection dynamically
  const loadPostmanCollection = async () => {
    try {
      setCollectionLoading(true);
      const response = await axios.get(`${API_BASE_URL}/postman/collection-data`);
      const collection = response.data;
      
      // Update collection variables with current user's data
      if (collection.variable && user && organizations) {
        const userOrgId = user.organizations?.[0]?.organization_id;
        if (userOrgId) {
          // Update organization-related variables with user's actual org
          collection.variable.forEach(variable => {
            if (variable.key === 'sample_org_id' || variable.key === 'admin_org_id') {
              variable.value = userOrgId;
            }
          });
        }
      }
      
      const parsedEndpoints = parsePostmanCollection(collection);
      setApiEndpoints(parsedEndpoints);
      
      // Auto-expand Authentication category
      setExpandedCategories(['Authentication']);
      
      console.log('✅ Loaded endpoints from collection:', Object.keys(parsedEndpoints));
      console.log('✅ Endpoints per category:', Object.entries(parsedEndpoints).map(([cat, eps]) => `${cat}: ${eps.length}`));
      console.log('✅ Using user organization ID:', user?.organizations?.[0]?.organization_id);
    } catch (error) {
      console.error('❌ Error loading Postman collection:', error);
      setError('Failed to load API endpoints from collection');
    } finally {
      setCollectionLoading(false);
    }
  };

  useEffect(() => {
    // Load Postman collection when user data is available
    if (user) {
      loadPostmanCollection();
    }
  }, [user]);

  useEffect(() => {
    // Update loading state based on collection loading
    setLoading(collectionLoading);
  }, [collectionLoading]);

  const handleTreeToggle = (event, nodeIds) => {
    setExpandedCategories(nodeIds);
  };

  const handleTreeSelect = (event, nodeId) => {
    setSelectedTreeNodes([nodeId]);

    // Check if it's an endpoint node (format: category-endpointIndex)
    if (nodeId.includes('-') && !nodeId.endsWith('-category')) {
      const [categoryName, endpointIndex] = nodeId.split('-');
      const endpoints = apiEndpoints[categoryName];
      if (endpoints && endpoints[parseInt(endpointIndex)]) {
        const endpoint = endpoints[parseInt(endpointIndex)];
        handleSelectEndpoint(endpoint);
      }
    }
  };

  const selectEndpoint = (endpoint) => {
    setSelectedEndpoint(endpoint);
    setRequestBody(endpoint.sampleBody || '');
    
    // Debug logging to see what params we have
    console.log('🔍 Selected endpoint params:', endpoint.params);
    console.log('🔍 Parameter metadata:', endpoint.parameterMetadata);
    
    // Initialize requestParams with empty values, not default values
    const initialParams = {};
    Object.keys(endpoint.params || {}).forEach(key => {
      initialParams[key] = ''; // Start with empty strings
    });
    setRequestParams(initialParams);
    setResponse(null);
    setError(null);
  };

  const executeRequest = async (endpoint) => {
    setRequestLoading(true);
    setResponse(null);
    setError(null);

    try {
      const headers = {
        'Content-Type': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` })
      };

      // Build URL with path parameters using current requestParams state
      let url = `${API_BASE_URL}${endpoint.path}`;
      const pathParams = {};
      const queryParams = {};
      
      // Separate path parameters from query parameters
      Object.keys(requestParams || {}).forEach(key => {
        const value = requestParams[key];
        if (url.includes(`{${key}}`)) {
          // Path parameters - use default if empty
          const paramValue = value || selectedEndpoint.params[key] || '';
          pathParams[key] = paramValue;
          url = url.replace(`{${key}}`, paramValue);
        } else if (value && value.toString().trim() !== '') {
          // Query parameters - only include if not empty
          queryParams[key] = value;
        }
      });

      // Add query parameters for GET requests only
      if (endpoint.method === 'GET' && Object.keys(queryParams).length > 0) {
        const searchParams = new URLSearchParams();
        Object.entries(queryParams).forEach(([key, value]) => {
          // Clean up the value to avoid malformed URLs
          const cleanValue = String(value).replace(/^\?/, '').trim();
          if (cleanValue) {
            searchParams.append(key, cleanValue);
          }
        });
        if (searchParams.toString()) {
          url += `?${searchParams.toString()}`;
        }
      }

      console.log('🔍 API Test Request:', {
        endpoint: endpoint.name,
        method: endpoint.method,
        finalUrl: url,
        pathParams,
        queryParams,
        hasAuth: !!token
      });

      let response;
      if (endpoint.method === 'POST' || endpoint.method === 'PUT') {
        response = await axios({
          method: endpoint.method,
          url,
          headers,
          data: requestBody ? JSON.parse(requestBody) : undefined
        });
      } else {
        response = await axios({
          method: endpoint.method,
          url,
          headers
        });
      }

      setResponse({
        status: response.status,
        statusText: response.statusText,
        headers: response.headers,
        data: response.data
      });

    } catch (err) {
      setResponse({
        status: err.response?.status || 0,
        statusText: err.response?.statusText || 'Network Error',
        headers: err.response?.headers || {},
        data: err.response?.data || { error: err.message }
      });
    } finally {
      setRequestLoading(false);
    }
  };

  const handleDownloadCollection = async () => {
    try {
      const link = document.createElement('a');
      link.href = `${API_BASE_URL}/postman/collection`;
      link.download = 'waltero-mimir-api.postman_collection.json';
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      setError('Failed to download collection. Please try again.');
      console.error('Collection download error:', err);
    }
  };

  const handleDownloadEnvironment = async (env) => {
    try {
      const link = document.createElement('a');
      link.href = `${API_BASE_URL}/postman/environments/${env}`;
      link.download = `waltero-mimir-${env}.postman_environment.json`;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      setError(`Failed to download ${env} environment. Please try again.`);
      console.error('Environment download error:', err);
    }
  };

  // Method to get HTTP method colors
  const getMethodColor = (method) => {
    const colors = {
      GET: theme.palette.waltero.primary.lightBlue,
      POST: theme.palette.waltero.secondary.primaryApricot,
      PUT: theme.palette.waltero.secondary.lightApricot,
      DELETE: theme.palette.waltero.secondary.darkApricot,
      PATCH: theme.palette.waltero.primary.mediumBlue
    };
    return colors[method] || theme.palette.grey[500];
  };

  // Enhanced description renderer
  const renderEndpointDescription = (description) => {
    if (!description) return null;

    // Split description into sections
    const sections = description.split('##').filter(section => section.trim());
    
    return (
      <Box>
        {sections.map((section, index) => {
          const lines = section.trim().split('\n').filter(line => line.trim());
          if (lines.length === 0) return null;

          // First section is the main description
          if (index === 0) {
            return (
              <WalteroTypography key={index} variant="body1" sx={{ mb: 2, lineHeight: 1.6 }}>
                {lines.join(' ')}
              </WalteroTypography>
            );
          }

          // Extract section title
          const titleLine = lines[0];
          const contentLines = lines.slice(1);
          
          // Handle table sections
          if (contentLines.some(line => line.includes('|'))) {
            return renderTableSection(titleLine, contentLines, index);
          }
          
          // Handle code blocks
          if (contentLines.some(line => line.includes('```'))) {
            return renderCodeSection(titleLine, contentLines, index);
          }
          
          // Handle regular sections
          return (
            <Box key={index} sx={{ mt: 3 }}>
              <WalteroTypography variant="h6" sx={{ mb: 1.5, color: theme.palette.waltero.primary.coreBlue, display: 'flex', alignItems: 'center', gap: 1 }}>
                {getIconForSection(titleLine)}
                {titleLine.replace(/[🔍📤📋🔐🔒📊🔄⚠️💡]/g, '').trim()}
              </WalteroTypography>
              <Box sx={{ pl: 0 }}>
                {contentLines.map((line, lineIndex) => (
                  <WalteroTypography key={lineIndex} variant="body2" sx={{ mb: 0.5 }}>
                    {line.replace(/^\s*-\s*/, '• ')}
                  </WalteroTypography>
                ))}
              </Box>
            </Box>
          );
        })}
      </Box>
    );
  };

  const getIconForSection = (title) => {
    if (title.includes('Query Parameters') || title.includes('Parameters')) return <Settings sx={{ fontSize: 20 }} />;
    if (title.includes('Response')) return <Code sx={{ fontSize: 20 }} />;
    if (title.includes('Access Control') || title.includes('Security')) return <Security sx={{ fontSize: 20 }} />;
    if (title.includes('Request Body') || title.includes('Schema')) return <Http sx={{ fontSize: 20 }} />;
    return <Info sx={{ fontSize: 20 }} />;
  };

  const renderTableSection = (title, contentLines, index) => {
    // Extract table headers and rows
    const tableLines = contentLines.filter(line => line.includes('|'));
    if (tableLines.length < 2) return null;

    const headers = tableLines[0].split('|').map(h => h.trim()).filter(h => h);
    const separatorIndex = tableLines.findIndex(line => line.includes('---'));
    const dataRows = tableLines.slice(separatorIndex + 1).map(line => 
      line.split('|').map(cell => cell.trim()).filter(cell => cell)
    );

    return (
      <Box key={index} sx={{ mt: 3 }}>
        <WalteroTypography variant="h6" sx={{ mb: 2, color: theme.palette.waltero.primary.coreBlue, display: 'flex', alignItems: 'center', gap: 1 }}>
          {getIconForSection(title)}
          {title.replace(/[🔍📤📋🔐🔒📊🔄⚠️💡]/g, '').trim()}
        </WalteroTypography>
        
        <WalteroCard variant="outlined" sx={{ overflow: 'hidden' }}>
          <Box sx={{ overflow: 'auto' }}>
            <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
              <Box component="thead" sx={{ backgroundColor: theme.palette.waltero.secondary.backgroundApricot }}>
                <Box component="tr">
                  {headers.map((header, headerIndex) => (
                    <Box 
                      key={headerIndex} 
                      component="th" 
                      sx={{ 
                        p: 2, 
                        textAlign: 'left', 
                        fontWeight: 600,
                        borderBottom: `1px solid ${theme.palette.divider}`,
                        fontSize: '0.875rem'
                      }}
                    >
                      {header}
                    </Box>
                  ))}
                </Box>
              </Box>
              <Box component="tbody">
                {dataRows.map((row, rowIndex) => (
                  <Box 
                    key={rowIndex} 
                    component="tr" 
                    sx={{ 
                      '&:nth-of-type(even)': { 
                        backgroundColor: theme.palette.background.default 
                      }
                    }}
                  >
                    {row.map((cell, cellIndex) => (
                      <Box 
                        key={cellIndex} 
                        component="td" 
                        sx={{ 
                          p: 2, 
                          borderBottom: `1px solid ${theme.palette.divider}`,
                          fontSize: '0.875rem',
                          fontFamily: cell.includes('`') ? 'monospace' : 'inherit',
                          color: cellIndex === 2 && (cell.includes('Yes') || cell.includes('No')) ? 
                            (cell.includes('Yes') ? theme.palette.success.main : theme.palette.text.secondary) : 
                            'inherit'
                        }}
                      >
                        {cell.replace(/`/g, '')}
                      </Box>
                    ))}
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        </WalteroCard>
      </Box>
    );
  };

  const renderCodeSection = (title, contentLines, index) => {
    let codeContent = '';
    let inCodeBlock = false;
    let language = 'json';

    contentLines.forEach(line => {
      if (line.includes('```')) {
        if (!inCodeBlock) {
          inCodeBlock = true;
          language = line.replace('```', '').trim() || 'json';
        } else {
          inCodeBlock = false;
        }
      } else if (inCodeBlock) {
        codeContent += line + '\n';
      }
    });

    return (
      <Box key={index} sx={{ mt: 3 }}>
        <WalteroTypography variant="h6" sx={{ mb: 2, color: theme.palette.waltero.primary.coreBlue, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Code sx={{ fontSize: 20 }} />
          {title.replace(/[🔍📤📋🔐🔒📊🔄⚠️💡]/g, '').trim()}
        </WalteroTypography>
        
        <Paper sx={{ 
          p: 2, 
          backgroundColor: theme.palette.background.default,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 2
        }}>
          <pre style={{ 
            margin: 0, 
            fontFamily: 'monospace', 
            fontSize: '0.875rem',
            whiteSpace: 'pre-wrap',
            color: theme.palette.text.primary
          }}>
            {codeContent.trim()}
          </pre>
        </Paper>
      </Box>
    );
  };

  // Enhanced select endpoint handler with accessibility
  const handleSelectEndpoint = (endpoint) => {
    selectEndpoint(endpoint);
    setAnimationKey(prev => prev + 1);
    
    // Announce to screen readers
    announceToScreenReader(`Selected ${endpoint.method} ${endpoint.path} endpoint`);
    
    // Auto-close mobile navigation
    if (responsive.breakpoints.isMobile) {
      setShowMobileNav(false);
    }
  };

  return (
    <Box 
      id={componentId}
      sx={{ 
        minHeight: '100vh',
        backgroundColor: theme.palette.waltero.primary.backgroundBlue,
        pb: 4
      }}
    >
      {/* Enhanced Hero Section */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.waltero.primary.deepBlue} 0%, ${theme.palette.waltero.primary.coreBlue} 100%)`,
          color: theme.palette.waltero.white,
          pt: { xs: 4, md: 6 },
          pb: { xs: 6, md: 8 },
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        {/* Background Pattern */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            right: 0,
            width: '50%',
            height: '100%',
            opacity: 0.1,
            background: `radial-gradient(ellipse at center, ${theme.palette.waltero.secondary.primaryApricot} 0%, transparent 70%)`
          }}
        />
        
        <Container maxWidth="xl" sx={{ position: 'relative' }}>
          <Fade in timeout={800}>
            <Box sx={{ textAlign: { xs: 'center', md: 'left' } }}>
              {/* Icon and Title */}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: { xs: 'center', md: 'flex-start' }, mb: 2 }}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: '16px',
                    backgroundColor: `${theme.palette.waltero.secondary.primaryApricot}20`,
                    border: `2px solid ${theme.palette.waltero.secondary.primaryApricot}`,
                    mr: 3,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <Api sx={{ fontSize: 48, color: theme.palette.waltero.secondary.primaryApricot }} />
                </Box>
                <Box>
                  <WalteroTypography 
                    variant="h1" 
                    sx={{ 
                      color: theme.palette.waltero.white,
                      fontSize: { xs: '2.5rem', md: '3.5rem' },
                      fontWeight: 800,
                      mb: 1
                    }}
                  >
                    API Documentation
                  </WalteroTypography>
                  <WalteroTypography variant="h6" sx={{ color: theme.palette.waltero.secondary.lightApricot, fontWeight: 400 }}>
                    Waltero Cloud Mimir Dashboard
                  </WalteroTypography>
                </Box>
              </Box>
              
              {/* Description */}
              <WalteroTypography 
                variant="body1" 
                sx={{ 
                  mb: 4, 
                  maxWidth: '600px',
                  fontSize: '1.125rem',
                  color: `${theme.palette.waltero.white}E6`,
                  lineHeight: 1.6
                }}
              >
                Complete REST API documentation for IoT device monitoring, real-time telemetry, 
                and cellular connectivity management. Test endpoints interactively with your authenticated session.
              </WalteroTypography>

              {/* Feature Highlights */}
              <Grid container spacing={3} sx={{ mb: 4, maxWidth: '800px' }}>
                {[
                  { icon: Security, label: 'Auto-Authenticated', description: 'Uses your current session' },
                  { icon: Speed, label: 'Interactive Testing', description: 'Test endpoints in real-time' },
                  { icon: IntegrationInstructions, label: 'Postman Ready', description: 'Download collections instantly' }
                ].map((feature, index) => (
                  <Grid item xs={12} sm={4} key={index}>
                    <Grow in timeout={1000 + (index * 200)}>
                      <Box sx={{ display: 'flex', alignItems: 'center', flexDirection: { xs: 'row', sm: 'column' } }}>
                        <feature.icon 
                          sx={{ 
                            fontSize: 32, 
                            color: theme.palette.waltero.secondary.lightApricot,
                            mb: { xs: 0, sm: 1 },
                            mr: { xs: 2, sm: 0 }
                          }} 
                        />
                        <Box sx={{ textAlign: { xs: 'left', sm: 'center' } }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600, color: theme.palette.waltero.white }}>
                            {feature.label}
                          </Typography>
                          <Typography variant="body2" sx={{ color: `${theme.palette.waltero.white}CC`, fontSize: '0.875rem' }}>
                            {feature.description}
                          </Typography>
                        </Box>
                      </Box>
                    </Grow>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Fade>
        </Container>
      </Box>

      <Container maxWidth="xl" sx={{ mt: 0 }}>

        {/* User Info Section - Top Section */}
        {user && (
          <Fade in timeout={1200}>
            <WalteroCard sx={{ mb: 4, p: 0 }}>
              {/* User Header */}
              <Box sx={{ 
                p: 3,
                backgroundColor: theme.palette.waltero.secondary.backgroundApricot,
                borderBottom: `1px solid ${theme.palette.divider}`
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        backgroundColor: `${theme.palette.waltero.secondary.primaryApricot}20`,
                        border: `2px solid ${theme.palette.waltero.secondary.primaryApricot}`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <Security sx={{ fontSize: 32, color: theme.palette.waltero.secondary.primaryApricot }} />
                    </Box>
                    <Box>
                      <WalteroTypography variant="h5" sx={{ fontWeight: 700, color: theme.palette.waltero.primary.coreBlue, mb: 0.5 }}>
                        Authenticated Session
                      </WalteroTypography>
                      <WalteroTypography variant="body1" sx={{ color: theme.palette.waltero.primary.mediumBlue, fontWeight: 500 }}>
                        {`${user.firstname && user.lastname ? `${user.firstname} ${user.lastname}` : user.email || 'User'}`}
                      </WalteroTypography>
                      <WalteroTypography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                        {user.organizations?.[0]?.organization_name || 'Active Organization'}
                      </WalteroTypography>
                    </Box>
                  </Box>
                  <WalteroButton
                    walteroVariant="primary"
                    startIcon={<Download />}
                    onClick={handleDownloadCollection}
                    size="medium"
                  >
                    Download Collection
                  </WalteroButton>
                </Box>
              </Box>
              
              {/* Quick Stats */}
              <Box sx={{ p: 3, backgroundColor: theme.palette.background.paper }}>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={4}>
                    <Box sx={{ textAlign: 'center' }}>
                      <WalteroTypography variant="h4" sx={{ color: theme.palette.waltero.primary.coreBlue, fontWeight: 800 }}>
                        {Object.keys(apiEndpoints).length}
                      </WalteroTypography>
                      <WalteroTypography variant="body2" sx={{ color: 'text.secondary' }}>
                        Endpoint Categories
                      </WalteroTypography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Box sx={{ textAlign: 'center' }}>
                      <WalteroTypography variant="h4" sx={{ color: theme.palette.waltero.secondary.primaryApricot, fontWeight: 800 }}>
                        {Object.values(apiEndpoints).reduce((total, endpoints) => total + endpoints.length, 0)}
                      </WalteroTypography>
                      <WalteroTypography variant="body2" sx={{ color: 'text.secondary' }}>
                        Total Endpoints
                      </WalteroTypography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Box sx={{ textAlign: 'center' }}>
                      <CheckCircle sx={{ fontSize: 32, color: theme.palette.success.main, mb: 1 }} />
                      <WalteroTypography variant="body2" sx={{ color: 'text.secondary' }}>
                        Session Active
                      </WalteroTypography>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </WalteroCard>
          </Fade>
        )}

        {/* Loading State */}
        {(loading || collectionLoading) && (
          <WalteroCard sx={{ textAlign: 'center', py: 8 }}>
            <CircularProgress size={60} sx={{ color: theme.palette.waltero.primary.coreBlue, mb: 2 }} />
            <WalteroTypography variant="h6" sx={{ mb: 1 }}>
              Loading API Documentation
            </WalteroTypography>
            <WalteroTypography variant="body2" color="text.secondary">
              Fetching endpoints from Postman collection...
            </WalteroTypography>
          </WalteroCard>
        )}

        {/* Error State */}
        {error && (
          <Alert 
            severity="error" 
            sx={{ 
              mb: 4,
              borderRadius: 2,
              '& .MuiAlert-icon': {
                color: theme.palette.waltero.secondary.darkApricot
              }
            }}
          >
            <WalteroTypography variant="body1" sx={{ fontWeight: 500 }}>
              {error}
            </WalteroTypography>
          </Alert>
        )}

        {/* Interactive API Explorer */}
        {!loading && !collectionLoading && Object.keys(apiEndpoints).length > 0 && (
          <Fade in timeout={800}>
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4, flexWrap: 'wrap', gap: 2 }}>
                <WalteroTypography variant="h3">
                  Interactive API Explorer
                </WalteroTypography>
                
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  <WalteroButton
                    walteroVariant={showAdminEndpoints ? "primary" : "outlined"}
                    startIcon={<Security />}
                    onClick={() => setShowAdminEndpoints(!showAdminEndpoints)}
                    size="small"
                  >
                    {showAdminEndpoints ? 'Hide Admin' : 'Show Admin'}
                  </WalteroButton>
                  
                  <WalteroButton
                    walteroVariant="outlined"
                    startIcon={<Refresh />}
                    onClick={() => loadPostmanCollection()}
                    size="small"
                  >
                    Refresh
                  </WalteroButton>
                </Box>
              </Box>
              
              {responsive.breakpoints.isMobile ? (
                // Mobile Layout
                <Box sx={{ minHeight: 'calc(100vh - 250px)' }}>
                  {/* Mobile Header */}
                  <WalteroCard sx={{ 
                    mb: 2,
                    p: 0,
                    backgroundColor: selectedEndpoint ? theme.palette.waltero.secondary.backgroundApricot : theme.palette.background.paper
                  }}>
                    <Box sx={{ p: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                        <WalteroTypography variant="h6" sx={{ color: theme.palette.waltero.primary.coreBlue }}>
                          {selectedEndpoint ? `${selectedEndpoint.method} ${selectedEndpoint.path}` : 'Select an Endpoint'}
                        </WalteroTypography>
                        <WalteroButton
                          walteroVariant="outlined"
                          size="small"
                          startIcon={showMobileNav ? <ExpandLess /> : <ExpandMore />}
                          onClick={() => setShowMobileNav(!showMobileNav)}
                        >
                          {showMobileNav ? 'Hide' : 'Browse'}
                        </WalteroButton>
                      </Box>
                      
                      {selectedEndpoint && (
                        <WalteroTypography variant="body2" sx={{ color: 'text.secondary' }}>
                          {selectedEndpoint.name}
                        </WalteroTypography>
                      )}
                    </Box>
                  </WalteroCard>
                  
                  {/* Mobile Navigation */}
                  <Collapse in={showMobileNav}>
                    <WalteroCard sx={{ mb: 3, p: 0 }}>
                      {renderEndpointTree()}
                    </WalteroCard>
                  </Collapse>
                  
                  {/* Mobile Content */}
                  <WalteroCard sx={{ 
                    minHeight: selectedEndpoint ? 'auto' : 'calc(100vh - 400px)',
                    p: 0
                  }}>
                    {renderEndpointContent()}
                  </WalteroCard>
                </Box>
              ) : (
                // Desktop Layout - Side by Side
                <Box sx={{ 
                  display: 'flex',
                  border: `1px solid ${theme.palette.divider}`, 
                  borderRadius: 3, 
                  overflow: 'hidden',
                  boxShadow: theme.shadows[2],
                  minHeight: 'calc(100vh - 300px)',
                  backgroundColor: theme.palette.background.paper
                }}>
                  {/* Left Panel - Endpoint Tree */}
                  <Box 
                    id={treeId}
                    sx={{ 
                      width: '350px',
                      minWidth: '350px',
                      maxWidth: '350px',
                      minHeight: 'calc(100vh - 320px)',
                      backgroundColor: theme.palette.background.paper,
                      borderRight: `1px solid ${theme.palette.divider}`,
                      overflow: 'auto'
                    }}
                  >
                    {renderEndpointTree()}
                  </Box>

                  {/* Right Panel - Documentation & Testing */}
                  <Box 
                    id={contentId}
                    sx={{ 
                      flex: 1,
                      minHeight: 'calc(100vh - 320px)',
                      backgroundColor: theme.palette.background.paper,
                      overflow: 'auto'
                    }}
                  >
                    {renderEndpointContent()}
                  </Box>
                </Box>
              )}
            </Box>
          </Fade>
        )}
      </Container>
    </Box>
  );

  // Enhanced tree view function with improved navigation and filtering
  function renderEndpointTree() {
    // Group endpoints by category for better organization
    const sortedCategories = Object.entries(apiEndpoints)
      .sort(([a], [b]) => {
        // Sort with Authentication first, then alphabetically
        if (a === 'Authentication') return -1;
        if (b === 'Authentication') return 1;
        return a.localeCompare(b);
      });

    return (
      <Box>
        {/* Header with search and controls */}
        <Box sx={{
          p: 3,
          backgroundColor: theme.palette.waltero.secondary.backgroundApricot,
          borderBottom: `1px solid ${theme.palette.divider}`
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <WalteroTypography variant="h6" sx={{
              display: 'flex',
              alignItems: 'center',
              color: theme.palette.waltero.primary.coreBlue
            }}>
              <MenuBook sx={{ mr: 2 }} />
              API Endpoints
            </WalteroTypography>

            {/* Expand/Collapse All Button */}
            <WalteroButton
              walteroVariant="outlined"
              size="small"
              onClick={() => {
                const allCategories = Object.keys(apiEndpoints);
                if (expandedCategories.length === allCategories.length) {
                  setExpandedCategories([]);
                } else {
                  setExpandedCategories(allCategories);
                }
              }}
              startIcon={expandedCategories.length === Object.keys(apiEndpoints).length ? <ExpandLess /> : <ExpandMore />}
            >
              {expandedCategories.length === Object.keys(apiEndpoints).length ? 'Collapse All' : 'Expand All'}
            </WalteroButton>
          </Box>

          {/* Quick Stats */}
          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Folder sx={{ fontSize: 16, color: theme.palette.waltero.primary.mediumBlue }} />
              <Typography variant="body2" sx={{ color: theme.palette.waltero.primary.mediumBlue, fontWeight: 500 }}>
                {Object.keys(apiEndpoints).length} Categories
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Api sx={{ fontSize: 16, color: theme.palette.waltero.primary.mediumBlue }} />
              <Typography variant="body2" sx={{ color: theme.palette.waltero.primary.mediumBlue, fontWeight: 500 }}>
                {Object.values(apiEndpoints).reduce((total, endpoints) => total + endpoints.length, 0)} Total Endpoints
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Security sx={{ fontSize: 16, color: theme.palette.waltero.secondary.primaryApricot }} />
              <Typography variant="body2" sx={{ color: theme.palette.waltero.primary.mediumBlue, fontWeight: 500 }}>
                {Object.values(apiEndpoints).reduce((total, endpoints) =>
                  total + endpoints.filter(endpoint => endpoint.isAdminOnly).length, 0)} Admin Only
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Enhanced Tree Structure */}
        <List sx={{ p: 0 }}>
          {sortedCategories.map(([category, endpoints]) => {
            const isExpanded = expandedCategories.includes(category);
            const visibleEndpoints = endpoints.filter(endpoint => showAdminEndpoints || !endpoint.isAdminOnly);
            const totalCount = endpoints.length;
            const adminCount = endpoints.filter(endpoint => endpoint.isAdminOnly).length;

            return (
              <Box key={category}>
                {/* Category Header */}
                <ListItemButton
                  onClick={() => {
                    if (isExpanded) {
                      setExpandedCategories(expandedCategories.filter(cat => cat !== category));
                    } else {
                      setExpandedCategories([...expandedCategories, category]);
                    }
                  }}
                  sx={{
                    backgroundColor: theme.palette.waltero.secondary.backgroundApricot,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    py: 2.5,
                    px: 3,
                    position: 'relative',
                    '&:hover': {
                      backgroundColor: theme.palette.waltero.secondary.paleApricot,
                    },
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      left: 0,
                      top: 0,
                      bottom: 0,
                      width: 4,
                      backgroundColor: isExpanded ? theme.palette.waltero.primary.coreBlue : 'transparent',
                      transition: 'background-color 0.2s ease'
                    }
                  }}
                >
                  {/* Category Icon */}
                  <Box sx={{ mr: 2, display: 'flex', alignItems: 'center' }}>
                    {isExpanded ?
                      <FolderOpen sx={{ color: theme.palette.waltero.primary.coreBlue, fontSize: 24 }} /> :
                      <Folder sx={{ color: theme.palette.waltero.primary.mediumBlue, fontSize: 24 }} />
                    }
                  </Box>

                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Typography variant="h6" sx={{
                          fontWeight: 700,
                          fontSize: '1rem',
                          color: theme.palette.waltero.primary.coreBlue,
                          letterSpacing: '0.02em'
                        }}>
                          {category}
                        </Typography>
                        {adminCount > 0 && (
                          <Chip
                            label={`${adminCount} Admin`}
                            size="small"
                            sx={{
                              backgroundColor: theme.palette.error.main,
                              color: theme.palette.waltero.white,
                              fontSize: '0.65rem',
                              fontWeight: 600,
                              height: 20
                            }}
                          />
                        )}
                      </Box>
                    }
                    secondary={
                      showAdminEndpoints ?
                        `${totalCount} endpoint${totalCount !== 1 ? 's' : ''}` :
                        `${visibleEndpoints.length} of ${totalCount} endpoint${totalCount !== 1 ? 's' : ''} visible`
                    }
                    secondaryTypographyProps={{
                      color: theme.palette.waltero.primary.mediumBlue,
                      fontSize: '0.85rem',
                      fontWeight: 500
                    }}
                  />

                  {/* Expand/Collapse Icon */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip
                      label={`${visibleEndpoints.length}`}
                      size="small"
                      sx={{
                        backgroundColor: theme.palette.waltero.primary.lightBlue,
                        color: theme.palette.waltero.primary.coreBlue,
                        fontSize: '0.7rem',
                        fontWeight: 700,
                        height: 22,
                        minWidth: 22
                      }}
                    />
                    <ChevronRight sx={{
                      color: theme.palette.waltero.primary.mediumBlue,
                      fontSize: 24,
                      transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                      transition: 'transform 0.2s ease'
                    }} />
                  </Box>
                </ListItemButton>

                {/* Endpoints List */}
                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding sx={{ backgroundColor: theme.palette.background.paper }}>
                    {visibleEndpoints.map((endpoint, index) => (
                      <ListItemButton
                        key={`${category}-${index}`}
                        selected={selectedEndpoint?.path === endpoint.path && selectedEndpoint?.method === endpoint.method}
                        onClick={() => handleSelectEndpoint(endpoint)}
                        sx={{
                          pl: 6,
                          pr: 3,
                          py: 2,
                          display: 'flex',
                          alignItems: 'center',
                          borderRadius: 0,
                          position: 'relative',
                          '&.Mui-selected': {
                            backgroundColor: `${theme.palette.waltero.primary.lightBlue}25`,
                            borderLeft: `4px solid ${theme.palette.waltero.primary.coreBlue}`,
                            '&:hover': {
                              backgroundColor: `${theme.palette.waltero.primary.lightBlue}35`,
                            }
                          },
                          '&:hover': {
                            backgroundColor: `${theme.palette.background.default}`,
                          },
                          '&::before': {
                            content: '""',
                            position: 'absolute',
                            left: 30,
                            top: 0,
                            bottom: 0,
                            width: 2,
                            backgroundColor: theme.palette.divider
                          },
                          '&::after': {
                            content: '""',
                            position: 'absolute',
                            left: 30,
                            top: '50%',
                            width: 16,
                            height: 2,
                            backgroundColor: theme.palette.divider,
                            transform: 'translateY(-50%)'
                          }
                        }}
                      >
                        {/* HTTP Method Badge */}
                        <Box sx={{ minWidth: 60, mr: 2 }}>
                          <Box
                            sx={{
                              px: 1.5,
                              py: 0.6,
                              borderRadius: 1.5,
                              backgroundColor: getMethodColor(endpoint.method),
                              color: theme.palette.waltero.white,
                              fontSize: '0.75rem',
                              fontWeight: 700,
                              minWidth: 56,
                              textAlign: 'center',
                              letterSpacing: '0.5px',
                              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                            }}
                          >
                            {endpoint.method}
                          </Box>
                        </Box>

                        {/* Endpoint Info */}
                        <ListItemText
                          primary={endpoint.name}
                          secondary={endpoint.path}
                          primaryTypographyProps={{
                            fontSize: '0.9rem',
                            fontWeight: 500,
                            color: theme.palette.waltero.primary.coreBlue
                          }}
                          secondaryTypographyProps={{
                            fontFamily: 'monospace',
                            fontSize: '0.8rem',
                            color: theme.palette.waltero.primary.mediumBlue
                          }}
                        />

                        {/* Status Badges */}
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, ml: 'auto' }}>
                          {endpoint.isAdminOnly && (
                            <Chip
                              label="Admin Only"
                              size="small"
                              sx={{
                                backgroundColor: theme.palette.error.main,
                                color: theme.palette.waltero.white,
                                fontSize: '0.65rem',
                                fontWeight: 700,
                                height: 18
                              }}
                            />
                          )}
                          {endpoint.requiresAuth && !endpoint.isAdminOnly && (
                            <Chip
                              label="Auth Required"
                              size="small"
                              sx={{
                                backgroundColor: theme.palette.waltero.secondary.primaryApricot,
                                color: theme.palette.waltero.white,
                                fontSize: '0.65rem',
                                fontWeight: 600,
                                height: 18
                              }}
                            />
                          )}
                        </Box>
                      </ListItemButton>
                    ))}
                  </List>
                </Collapse>
              </Box>
            );
          })}
        </List>
      </Box>
    );
  }

  // Render endpoint content function
  function renderEndpointContent() {
    if (!selectedEndpoint) {
      return (
        <Box sx={{ p: 4, textAlign: 'center', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <Api sx={{ fontSize: 80, color: theme.palette.text.disabled, mb: 3 }} />
          <WalteroTypography variant="h4" sx={{ color: theme.palette.text.secondary, mb: 2 }}>
            Select an Endpoint
          </WalteroTypography>
          <WalteroTypography variant="body1" sx={{ color: 'text.secondary', maxWidth: '400px', mx: 'auto' }}>
            Choose an endpoint from the navigation to view documentation and test it interactively with your current session.
          </WalteroTypography>
        </Box>
      );
    }

    return (
      <Fade in key={animationKey} timeout={300}>
        <Box>
          {/* Endpoint Header */}
          <Box sx={{ 
            p: 3, 
            backgroundColor: theme.palette.waltero.secondary.backgroundApricot,
            borderBottom: `1px solid ${theme.palette.divider}`
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
              <Box
                sx={{
                  px: 2,
                  py: 1,
                  borderRadius: 2,
                  backgroundColor: getMethodColor(selectedEndpoint.method),
                  color: theme.palette.waltero.white,
                  fontWeight: 600
                }}
              >
                {selectedEndpoint.method}
              </Box>
              <WalteroTypography 
                variant="h5" 
                sx={{ 
                  fontFamily: 'monospace', 
                  fontWeight: 600,
                  color: theme.palette.waltero.primary.coreBlue,
                  wordBreak: 'break-all'
                }}
              >
                {selectedEndpoint.path}
              </WalteroTypography>
              {selectedEndpoint.requiresAuth && (
                <WalteroStatusChip 
                  label="Auth Required" 
                  color="warning" 
                  size="small"
                  icon={<Security />}
                />
              )}
              {selectedEndpoint.isAdminOnly && (
                <WalteroStatusChip 
                  label="Admin Only" 
                  color="error" 
                  size="small"
                  icon={<Security />}
                />
              )}
            </Box>
            {/* Enhanced Description Rendering */}
            <Box sx={{ color: 'text.secondary' }}>
              {renderEndpointDescription(selectedEndpoint.description)}
            </Box>
          </Box>

          {/* Request Configuration */}
          <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <WalteroTypography variant="h6">
                Request Configuration
              </WalteroTypography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <WalteroButton
                  walteroVariant={showRequestDetails ? "primary" : "outlined"}
                  size="small"
                  startIcon={showRequestDetails ? <VisibilityOff /> : <Visibility />}
                  onClick={() => setShowRequestDetails(!showRequestDetails)}
                >
                  {showRequestDetails ? 'Hide Details' : 'Show Details'}
                </WalteroButton>
              </Box>
            </Box>

            {/* Headers Section */}
            <Collapse in={showRequestDetails}>
              <WalteroCard variant="outlined" sx={{ mb: 3 }}>
                <Box sx={{ p: 3 }}>
                  <WalteroTypography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Http /> Headers
                    <IconButton 
                      size="small" 
                      onClick={() => setHelpTooltips(prev => ({ ...prev, headers: !prev.headers }))}
                    >
                      <Help fontSize="small" />
                    </IconButton>
                  </WalteroTypography>
                  
                  <Collapse in={helpTooltips.headers}>
                    <Alert severity="info" sx={{ mb: 2 }}>
                      Headers are automatically included in requests. The Authorization header uses your current JWT token.
                    </Alert>
                  </Collapse>

                  <Box sx={{ backgroundColor: theme.palette.background.default, p: 2, borderRadius: 1, border: `1px solid ${theme.palette.divider}` }}>
                    <pre style={{ margin: 0, fontFamily: 'monospace', fontSize: '0.875rem' }}>
{Object.entries(requestHeaders).map(([key, value]) => `${key}: ${value}`).join('\n')}
                    </pre>
                  </Box>
                  
                  <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                    <WalteroButton
                      walteroVariant="outlined"
                      size="small"
                      startIcon={<ContentCopy />}
                      onClick={() => navigator.clipboard.writeText(Object.entries(requestHeaders).map(([key, value]) => `${key}: ${value}`).join('\n'))}
                    >
                      Copy Headers
                    </WalteroButton>
                  </Box>
                </Box>
              </WalteroCard>
            </Collapse>
            
            {/* Parameters - Always show if endpoint selected, even if empty */}
            {selectedEndpoint && (
              <WalteroCard variant="outlined" sx={{ mb: 3 }}>
                <Box sx={{ p: 3 }}>
                  <WalteroTypography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Settings /> Parameters
                    <IconButton 
                      size="small" 
                      onClick={() => setHelpTooltips(prev => ({ ...prev, params: !prev.params }))}
                    >
                      <Help fontSize="small" />
                    </IconButton>
                  </WalteroTypography>
                  
                  <Collapse in={helpTooltips.params}>
                    <Alert severity="info" sx={{ mb: 2 }}>
                      Parameters are used in URL paths (e.g., /devices/&#123;id&#125;) or as query parameters (?page=1&limit=20). Required parameters must be provided.
                    </Alert>
                  </Collapse>

                  {Object.keys(selectedEndpoint.params || {}).length > 0 ? (
                    <Grid container spacing={2}>
                      {Object.entries(selectedEndpoint.params).map(([key, value]) => {
                      // Get metadata for this parameter
                      const metadata = selectedEndpoint.parameterMetadata?.[key];
                      
                      // Determine parameter type based on path usage or metadata
                      const isPathParam = selectedEndpoint.path.includes(`{${key}}`);
                      let paramType = 'Query Parameter';
                      
                      if (metadata?.parameterType === 'path' || isPathParam) {
                        paramType = 'Path Parameter';
                      } else if (metadata?.parameterType === 'query') {
                        paramType = 'Query Parameter';
                      } else if (metadata?.parameterType === 'body') {
                        paramType = 'Body Field';
                      }
                      
                      // Determine input type based on parameter data type
                      let inputType = 'text';
                      let inputProps = {};
                      
                      if (metadata?.type.toLowerCase().includes('integer') || metadata?.type.toLowerCase().includes('number')) {
                        inputType = 'number';
                        inputProps.min = 0;
                      } else if (metadata?.type.toLowerCase().includes('boolean')) {
                        inputType = 'select';
                      } else if (metadata?.type.toLowerCase().includes('enum')) {
                        inputType = 'select';
                      } else if (key.toLowerCase().includes('date') || metadata?.description?.toLowerCase().includes('date') || metadata?.example?.match(/\d{4}-\d{2}-\d{2}/)) {
                        inputType = 'date';
                      }
                      
                      const helperText = [
                        paramType,
                        metadata?.required ? 'Required' : 'Optional',
                        metadata?.type ? `Type: ${metadata.type}` : '',
                        metadata?.description || `Example: ${value}`
                      ].filter(Boolean).join(' • ');
                      
                      // Extract enum options from description if available
                      const getEnumOptions = () => {
                        if (metadata?.type.toLowerCase().includes('boolean')) {
                          return [
                            { value: '', label: 'Select...' },
                            { value: 'true', label: 'true' },
                            { value: 'false', label: 'false' }
                          ];
                        }
                        
                        // Extract enum options from description like: "daily, weekly, monthly"
                        if (metadata?.description?.includes(':') && metadata?.description?.includes(',')) {
                          const descPart = metadata.description.split(':').pop();
                          if (descPart && descPart.includes('`')) {
                            const options = descPart.match(/`([^`]+)`/g);
                            if (options) {
                              return [
                                { value: '', label: 'Select...' },
                                ...options.map(opt => {
                                  const cleanOpt = opt.replace(/`/g, '');
                                  return { value: cleanOpt, label: cleanOpt };
                                })
                              ];
                            }
                          }
                        }
                        
                        return [{ value: '', label: 'Select...' }];
                      };

                      return (
                        <Grid item xs={12} sm={6} key={key}>
                          {inputType === 'select' ? (
                            <TextField
                              fullWidth
                              size="small"
                              select
                              label={key}
                              value={requestParams[key] !== undefined ? requestParams[key] : value}
                              onChange={(e) => setRequestParams(prev => ({ ...prev, [key]: e.target.value }))}
                              variant="outlined"
                              helperText={helperText}
                              SelectProps={{
                                native: true,
                              }}
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                    borderColor: theme.palette.waltero.secondary.primaryApricot
                                  }
                                }
                              }}
                            >
                              {getEnumOptions().map(option => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </TextField>
                          ) : (
                            <TextField
                              fullWidth
                              size="small"
                              type={inputType}
                              label={key}
                              value={requestParams[key] !== undefined ? requestParams[key] : ''}
                              onChange={(e) => setRequestParams(prev => ({ ...prev, [key]: e.target.value }))}
                              variant="outlined"
                              helperText={helperText}
                              placeholder={value ? `Example: ${value}` : metadata?.example || ''}
                              required={metadata?.required}
                              error={metadata?.required && !requestParams[key]}
                              inputProps={inputProps}
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                    borderColor: theme.palette.waltero.secondary.primaryApricot
                                  }
                                }
                              }}
                            />
                          )}
                        </Grid>
                      );
                    })}
                    </Grid>
                  ) : (
                    <Alert severity="info">
                      <strong>Debug Info:</strong> No parameters extracted for this endpoint.
                      <br />
                      <small>
                        Extracted params: {JSON.stringify(Object.keys(selectedEndpoint.params || {}))}<br />
                        Has metadata: {Object.keys(selectedEndpoint.parameterMetadata || {}).length > 0 ? 'Yes' : 'No'}<br />
                        Metadata keys: {JSON.stringify(Object.keys(selectedEndpoint.parameterMetadata || {}))}
                      </small>
                    </Alert>
                  )}
                </Box>
              </WalteroCard>
            )}

            {/* Request Body */}
            {(selectedEndpoint.method === 'POST' || selectedEndpoint.method === 'PUT') && (
              <WalteroCard variant="outlined" sx={{ mb: 3 }}>
                <Box sx={{ p: 3 }}>
                  <WalteroTypography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Code /> Request Body (JSON)
                    <IconButton 
                      size="small" 
                      onClick={() => setHelpTooltips(prev => ({ ...prev, body: !prev.body }))}
                    >
                      <Help fontSize="small" />
                    </IconButton>
                  </WalteroTypography>
                  
                  <Collapse in={helpTooltips.body}>
                    <Alert severity="info" sx={{ mb: 2 }}>
                      The request body should be valid JSON. Use the sample provided as a template and modify values as needed. Required fields are marked in the documentation above.
                    </Alert>
                  </Collapse>

                  <TextField
                    fullWidth
                    multiline
                    rows={8}
                    variant="outlined"
                    value={requestBody}
                    onChange={(e) => setRequestBody(e.target.value)}
                    placeholder="Enter JSON request body..."
                    helperText="JSON format required • Modify the sample data above"
                    sx={{ 
                      '& .MuiInputBase-input': { 
                        fontFamily: 'monospace', 
                        fontSize: '0.875rem' 
                      },
                      '& .MuiOutlinedInput-root': {
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: theme.palette.waltero.secondary.primaryApricot
                        }
                      }
                    }}
                  />
                  
                  <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                    <WalteroButton
                      walteroVariant="outlined"
                      size="small"
                      startIcon={<ContentCopy />}
                      onClick={() => navigator.clipboard.writeText(requestBody)}
                      disabled={!requestBody}
                    >
                      Copy Body
                    </WalteroButton>
                    <WalteroButton
                      walteroVariant="outlined"
                      size="small"
                      onClick={() => {
                        try {
                          const formatted = JSON.stringify(JSON.parse(requestBody), null, 2);
                          setRequestBody(formatted);
                        } catch (e) {
                          // Handle invalid JSON gracefully
                        }
                      }}
                      disabled={!requestBody}
                    >
                      Format JSON
                    </WalteroButton>
                  </Box>
                </Box>
              </WalteroCard>
            )}

            {/* Execute Button */}
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-start' }}>
              <WalteroButton
                walteroVariant="primary"
                size="large"
                startIcon={requestLoading ? <CircularProgress size={16} sx={{ color: 'white' }} /> : <Send />}
                onClick={() => executeRequest(selectedEndpoint)}
                disabled={requestLoading}
              >
                {requestLoading ? 'Executing...' : 'Execute Request'}
              </WalteroButton>
              
              {response && (
                <WalteroButton
                  walteroVariant="outlined"
                  size="large"
                  onClick={() => setResponse(null)}
                >
                  Clear Response
                </WalteroButton>
              )}
            </Box>
          </Box>

          {/* Response Section */}
          {response && (
            <Slide in={!!response} direction="up" timeout={300}>
              <Box sx={{ 
                p: 3, 
                backgroundColor: theme.palette.background.default, 
                borderTop: `1px solid ${theme.palette.divider}`
              }}>
                <WalteroTypography variant="h6" sx={{ mb: 3 }}>
                  Response
                </WalteroTypography>
                
                <Box sx={{ mb: 3 }}>
                  <WalteroStatusChip 
                    label={`${response.status} ${response.statusText}`}
                    color={response.status >= 200 && response.status < 300 ? 'success' : 'error'}
                  />
                </Box>

                <WalteroTypography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                  Response Body:
                </WalteroTypography>
                <Paper 
                  sx={{ 
                    p: 3, 
                    backgroundColor: response.status >= 200 && response.status < 300 ? 
                      `${theme.palette.success.main}08` : 
                      `${theme.palette.error.main}08`,
                    fontFamily: 'monospace',
                    fontSize: '0.875rem',
                    maxHeight: '400px',
                    overflow: 'auto',
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 2
                  }}
                >
                  <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                    {JSON.stringify(response.data, null, 2)}
                  </pre>
                </Paper>
              </Box>
            </Slide>
          )}
        </Box>
      </Fade>
    );
  }
};

export default ApiDocumentation;